import {
  BankOutlined,
  BellOutlined,
  DashboardOutlined,
  ExperimentOutlined,
  CheckOutlined,
  GlobalOutlined,
  LockOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Badge, Button, Dropdown, Space, Tooltip, message } from 'antd';
import type { MenuProps } from 'antd';
import { ProLayout, type MenuDataItem } from '@ant-design/pro-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  localizedSystemName,
  setTenantRuntimeSettings,
  type TenantRuntimeSettings,
} from '../lib/tenant';
import { DeploymentAccessBadge } from '../components/DeploymentAccessBadge';

type NotificationsSummary = { unreadCount: number };
type CurrentUserSummary = {
  tenant?: TenantRuntimeSettings;
  permissions: string[];
  username: string;
  displayName?: string;
};

type AppRoute = Omit<MenuDataItem, 'children' | 'routes'> & {
  children?: AppRoute[];
  routes?: AppRoute[];
};

const languageOptions = [
  { key: 'zh-CN', flag: '🇨🇳', labelKey: 'common.languages.zhCN' },
  { key: 'en-US', flag: '🇺🇸', labelKey: 'common.languages.enUS' },
] as const;

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState<number>();
  const [systemNames, setSystemNames] = useState<TenantRuntimeSettings>();
  const [logoUrl, setLogoUrl] = useState<string>();
  const [currentUser, setCurrentUser] = useState<CurrentUserSummary>();
  const [loggingOut, setLoggingOut] = useState(false);
  const [messageApi, messageHolder] = message.useMessage();
  const { t, i18n } = useTranslation();
  const systemName = localizedSystemName(
    systemNames,
    i18n.resolvedLanguage,
    t('app.name'),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const changeLanguage = async (value: 'zh-CN' | 'en-US') => {
    const response = await fetch('/api/v1/auth/me/locale', {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ locale: value }),
    });
    if (response.ok) await i18n.changeLanguage(value);
  };
  useEffect(() => {
    const loadTenantSettings = async () => {
      try {
        const response = await fetch('/api/v1/auth/me', {
          credentials: 'include',
        });
        if (!response.ok) return;
        const user = (await response.json()) as CurrentUserSummary;
        setCurrentUser(user);
        setTenantRuntimeSettings(user.tenant);
        setSystemNames(user.tenant);
        setLogoUrl(user.tenant?.logoUrl ?? undefined);
      } catch {
        setTenantRuntimeSettings(undefined);
        setSystemNames(undefined);
      }
    };
    const loadUnreadCount = async () => {
      try {
        const response = await fetch('/api/v1/notifications?unreadOnly=true', {
          credentials: 'include',
        });
        if (!response.ok) {
          setUnreadCount(undefined);
          return;
        }
        const data = (await response.json()) as NotificationsSummary;
        setUnreadCount(data.unreadCount);
      } catch {
        setUnreadCount(undefined);
      }
    };
    const refresh = () => void loadUnreadCount();
    const refreshBranding = () => void loadTenantSettings();
    void loadTenantSettings();
    refresh();
    window.addEventListener('tenant:branding-changed', refreshBranding);
    window.addEventListener('notifications:changed', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('tenant:branding-changed', refreshBranding);
      window.removeEventListener('notifications:changed', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [location.pathname]);
  useEffect(() => {
    document.title = systemName;
  }, [systemName]);
  const hasPermission = (permission: string) =>
    currentUser?.permissions.includes(permission) ?? false;
  const logout = async () => {
    setLoggingOut(true);
    try {
      await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      await messageApi.success(t('user.logoutSuccess'));
    } catch {
      await messageApi.warning(t('user.logoutFallback'));
    } finally {
      setLoggingOut(false);
      setCurrentUser(undefined);
      setUnreadCount(undefined);
      void navigate('/login', { replace: true });
    }
  };
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'account',
      icon: <LockOutlined />,
      label: t('user.accountSettings'),
      onClick: () => void navigate('/account?tab=settings'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      danger: true,
      icon: <LogoutOutlined />,
      label: t('user.logout'),
      onClick: () => void logout(),
    },
  ];
  const languageMenuItems: MenuProps['items'] = languageOptions.map(
    ({ key, flag, labelKey }) => ({
      key,
      icon:
        (i18n.resolvedLanguage ?? 'zh-CN') === key ? (
          <CheckOutlined />
        ) : (
          <span className="app-language-check-placeholder" aria-hidden="true" />
        ),
      label: (
        <span className="app-language-menu-item">
          <span className="app-language-flag" aria-hidden="true">
            {flag}
          </span>
          <span>{t(labelKey)}</span>
        </span>
      ),
      onClick: () => void changeLanguage(key),
    }),
  );
  const visibleMenuItems = <T,>(items: Array<T | null>): T[] =>
    items.filter((item): item is T => item !== null);
  const catalogChildren: AppRoute[] = visibleMenuItems([
    hasPermission('cell_line:read')
      ? {
          key: '/cell-lines',
          path: '/cell-lines',
          name: t('nav.cellLines'),
        }
      : null,
    hasPermission('cell_line:read')
      ? {
          key: '/materials',
          path: '/materials',
          name: t('nav.materials'),
        }
      : null,
    hasPermission('sample:read')
      ? { key: '/samples', path: '/samples', name: t('nav.samples') }
      : null,
    hasPermission('storage:manage') || hasPermission('sample:create')
      ? { key: '/storage', path: '/storage', name: t('nav.storage') }
      : null,
    hasPermission('qc:read') || hasPermission('qc:manage')
      ? { key: '/quality', path: '/quality', name: t('nav.qualityRecords') }
      : null,
  ]);
  const canSearch =
    hasPermission('sample:read') ||
    hasPermission('cell_line:read') ||
    hasPermission('audit:read');
  const workflowChildren: AppRoute[] = visibleMenuItems([
    hasPermission('inventory:request') ||
    hasPermission('inventory:approve') ||
    hasPermission('inventory:execute')
      ? { key: '/requests', path: '/requests', name: t('nav.requests') }
      : null,
    hasPermission('inventory:approve')
      ? { key: '/approvals', path: '/approvals', name: t('nav.approvals') }
      : null,
    hasPermission('audit:read')
      ? {
          key: '/transactions',
          path: '/transactions',
          name: t('nav.transactions'),
        }
      : null,
  ]);
  const systemChildren: AppRoute[] = visibleMenuItems([
    hasPermission('identity:read')
      ? { key: '/system', path: '/system', name: t('nav.admin') }
      : null,
    hasPermission('identity:manage') || hasPermission('storage:manage')
      ? {
          key: '/maintenance',
          path: '/maintenance',
          name: t('nav.maintenanceTools'),
        }
      : null,
    hasPermission('identity:read')
      ? { key: '/users', path: '/users', name: t('nav.users') }
      : null,
    hasPermission('audit:read')
      ? { key: '/audit', path: '/audit', name: t('nav.audit') }
      : null,
    hasPermission('cell_line_catalog:manage')
      ? {
          key: '/cell-line-catalog',
          path: '/cell-line-catalog',
          name: t('nav.cellLineCatalog'),
        }
      : null,
  ]);
  const dashboardChildren: AppRoute[] = [
    {
      key: '/dashboard/workplace',
      path: '/dashboard/workplace',
      name: t('nav.workplace'),
    },
    {
      key: '/dashboard/board',
      path: '/dashboard/board',
      name: t('nav.board'),
    },
  ];
  const menuItems: AppRoute[] = visibleMenuItems([
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      name: t('nav.overview'),
      children: dashboardChildren,
    },
    canSearch
      ? {
          key: '/search',
          path: '/search',
          icon: <SearchOutlined />,
          name: t('nav.search'),
        }
      : null,
    catalogChildren.length
      ? {
          key: 'catalog',
          icon: <ExperimentOutlined />,
          name: t('nav.catalog'),
          children: catalogChildren,
        }
      : null,
    workflowChildren.length
      ? {
          key: 'workflow',
          icon: <SwapOutlined />,
          name: t('nav.workflow'),
          children: workflowChildren,
        }
      : null,
    systemChildren.length
      ? {
          key: 'system',
          icon: <SettingOutlined />,
          name: t('nav.systemGroup'),
          children: systemChildren,
        }
      : null,
  ]);

  return (
    <ProLayout
      className="app-pro-layout"
      logo={
        logoUrl ? (
          <img
            src={logoUrl}
            alt={systemName || t('app.name')}
            className="app-tenant-logo"
          />
        ) : (
          <BankOutlined />
        )
      }
      title={systemName || t('app.name')}
      layout="mix"
      splitMenus={false}
      navTheme="light"
      siderWidth={248}
      fixSiderbar
      fixedHeader
      collapsed={collapsed}
      onCollapse={setCollapsed}
      location={{ pathname: location.pathname }}
      route={{ path: '/', routes: menuItems }}
      menu={{ defaultOpenAll: true }}
      menuItemRender={(item, defaultDom) =>
        item.path ? <Link to={item.path}>{defaultDom}</Link> : defaultDom
      }
      onMenuHeaderClick={() => void navigate('/')}
      avatarProps={false}
      actionsRender={() => (
        <Space size={12} className="app-pro-layout-actions">
          <DeploymentAccessBadge />
          <Tooltip title={t('nav.notifications')}>
            <Badge count={unreadCount} size="small">
              <Button
                type="text"
                shape="circle"
                aria-label={t('nav.notifications')}
                icon={<BellOutlined />}
                onClick={() => void navigate('/notifications')}
              />
            </Badge>
          </Tooltip>
          <Tooltip title={t('nav.help')}>
            <Button
              type="text"
              shape="circle"
              aria-label={t('nav.help')}
              icon={<QuestionCircleOutlined />}
              onClick={() => void navigate('/help')}
            />
          </Tooltip>
          <Dropdown
            menu={{
              items: languageMenuItems,
              selectedKeys: [i18n.resolvedLanguage ?? 'zh-CN'],
            }}
            classNames={{ root: 'app-language-dropdown' }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button
              type="text"
              shape="circle"
              aria-label={t('common.language')}
              title={t('common.language')}
              icon={<GlobalOutlined />}
            />
          </Dropdown>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Button type="text" loading={loggingOut}>
              <Space size={8}>
                <Avatar size="small" icon={<UserOutlined />} />
                <span>{currentUser?.displayName ?? currentUser?.username}</span>
              </Space>
            </Button>
          </Dropdown>
        </Space>
      )}
      contentStyle={{
        minHeight: 'calc(100vh - 64px)',
        padding: 24,
        background: '#f5f5f5',
      }}
      footerRender={false}
    >
      {messageHolder}
      <Outlet />
    </ProLayout>
  );
}
